/**
 * Curated list of phrases that commonly trip Suno's content filter.
 *
 * Used by the "Prompt Rejected" panel on SongDetail.jsx to highlight
 * suspected trigger words in the user's rejected prompt, so they can
 * see at-a-glance what to edit.
 *
 * Updating the list:
 *   - Add real-world rejections as you collect them (admin sees rejected
 *     prompts in the song's failure panel + DB).
 *   - Categories below are advisory — they share the same highlight
 *     style, but help us keep this file organised.
 *   - Patterns are matched **case-insensitive** with **word boundaries**
 *     so "Queen" matches "Queen" but not "Queensland".
 *
 * Heuristic, not exhaustive: this is a hint to the user, not a hard
 * block. The real arbiter is Suno's filter — we just point users at
 * the most likely offender so editing is obvious.
 */

// === Famous artists (most commonly rejected by Suno) ===
// Only include band/artist names that are unlikely to appear in
// everyday English. "Kiss" the band is excluded because users say
// "give her a kiss" in romantic prompts; "Prince" is risky for the
// same reason but included with word-boundary protection (it's
// usually capitalised when meant as the artist).
const ARTISTS = [
  'taylor swift', 'beyonce', 'beyoncé', 'drake', 'the beatles', 'beatles',
  'queen', 'freddie mercury', 'michael jackson', 'eminem', 'rihanna',
  'adele', 'ed sheeran', 'elton john', 'madonna', 'prince', 'david bowie',
  'bob dylan', 'paul mccartney', 'john lennon', 'mick jagger',
  'rolling stones', 'led zeppelin', 'pink floyd', 'metallica', 'nirvana',
  'bruno mars', 'lady gaga', 'katy perry', 'justin bieber', 'ariana grande',
  'billie eilish', 'olivia rodrigo', 'sza', 'the weeknd', 'post malone',
  'kanye west', 'kendrick lamar', 'jay-z', 'jay z', 'nicki minaj',
  'cardi b', 'travis scott', 'frank sinatra', 'elvis presley', 'elvis',
  'johnny cash', 'dolly parton', 'willie nelson', 'garth brooks',
  'bruce springsteen', 'tom petty', 'jimi hendrix', 'bob marley',
  'whitney houston', 'mariah carey', 'celine dion', 'shania twain',
  'coldplay', 'maroon 5', 'imagine dragons', 'twenty one pilots',
  'foo fighters', 'red hot chili peppers', 'green day', 'linkin park',
  'ac/dc', 'guns n roses', "guns n' roses", 'aerosmith', 'the who',
  'simon and garfunkel', 'fleetwood mac', 'abba',
  'bts', 'blackpink',
];

// === Iconic song titles (frequently rejected when used as a prompt) ===
// IMPORTANT: only include phrases that are 2+ words OR sufficiently
// uncommon that they won't false-positive in everyday English.
// Single common words like "happy", "imagine", "yesterday" are
// EXCLUDED here — they're also normal English words and would
// over-flag clean prompts.
const SONGS = [
  'bohemian rhapsody', 'hotel california', 'stairway to heaven',
  'let it be', 'hey jude', 'smells like teen spirit',
  'sweet child o mine', 'november rain', 'thunderstruck', 'back in black',
  'we will rock you', 'we are the champions', 'dont stop believin',
  "don't stop believin'", 'sweet caroline', 'piano man', 'wonderwall',
  'losing my religion',
  'shape of you', 'blinding lights', 'despacito', 'gangnam style',
  'old town road', 'rolling in the deep', 'someone like you',
  'uptown funk', 'shake it off', 'bad guy', 'as it was',
  'anti-hero', 'anti hero', 'cruel summer',
];

// === Brands / IP that trigger trademark filters ===
const BRANDS = [
  'disney', 'mickey mouse', 'pixar', 'marvel', 'star wars',
  'harry potter', 'pokemon', 'pokémon', 'nintendo', 'mario', 'zelda',
  'coca-cola', 'coca cola', 'pepsi', 'mcdonalds', "mcdonald's",
  'nike', 'adidas', 'apple inc', 'google', 'facebook', 'tiktok',
  'james bond', '007', 'batman', 'superman', 'spider-man', 'spiderman',
];

// === Explicit / violent / restricted terms ===
const RESTRICTED = [
  'kill', 'murder', 'suicide', 'rape', 'shooting', 'massacre',
  'terrorist', 'isis', 'nazi', 'heroin', 'cocaine', 'meth',
];

// Build a single de-duplicated lowercase list
const ALL_TRIGGERS = Array.from(
  new Set([...ARTISTS, ...SONGS, ...BRANDS, ...RESTRICTED].map((s) => s.toLowerCase()))
);

// Escape regex metachars in user-supplied phrases
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Combined regex — case-insensitive, longest-match-first to avoid
// partial overlaps (e.g. "Taylor Swift" before "Taylor").
const sortedByLength = [...ALL_TRIGGERS].sort((a, b) => b.length - a.length);
const TRIGGER_REGEX = new RegExp(
  `\\b(${sortedByLength.map(escapeRegex).join('|')})\\b`,
  'gi'
);

/**
 * Return an array of React-renderable segments from a string, with
 * matched trigger words wrapped in a highlighted <mark>.
 *
 * @param {string} text  The (potentially rejected) prompt text.
 * @returns {Array<string|JSX.Element>}  Mixed array suitable for React.
 */
export function highlightTriggers(text) {
  if (!text) return [];
  const out = [];
  let lastIndex = 0;
  let match;
  // Reset the regex so we can reuse the same instance across calls
  TRIGGER_REGEX.lastIndex = 0;
  let key = 0;
  while ((match = TRIGGER_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    out.push(
      <mark
        key={`t-${key++}-${match.index}`}
        className="bg-red-500/25 text-red-200 px-1 rounded font-semibold border border-red-500/40"
        title="Likely trigger for Suno's content filter"
      >
        {match[0]}
      </mark>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out.length ? out : [text];
}

/** Exposed for tests / admin tooling. */
export const _internals = { ALL_TRIGGERS, TRIGGER_REGEX };
